import styled from '@emotion/styled';
import { Link } from 'react-router-dom';

export const OrderCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border: 1px solid #f0f0f0;
  margin-bottom: 8px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  }
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  gap: 12px;
  background: #fafafa;
`;

export const VendorImage = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: #f5f5f5;
  border: 1px solid #eee;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const HeaderInfo = styled.div`
  flex-grow: 1;
  min-width: 0;
`;

export const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
`;

export const VendorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

export const VendorName = styled.h3`
  font-size: 14px;
  font-weight: 600;
  margin: 0;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const OrderRef = styled.span`
  font-size: 12px;
  color: #666;
  font-weight: 500;
`;

export const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const StatusBadge = styled.span<{ status: string }>`
  display: inline-flex;
  padding: 2px 8px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 500;
  align-items: center;
  text-transform: uppercase;

  ${({ status }) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return `
          background-color: #e6f7e6;
          color: #2e7d32;
        `;
      case 'pending':
        return `
          background-color: #fff8e1;
          color: #f57c00;
        `;
      case 'ongoing':
        return `
          background-color: #e3f2fd;
          color: #1976d2;
        `;
      case 'cancelled':
        return `
          background-color: #feebee;
          color: #c62828;
        `;
      default:
        return `
          background-color: #f5f5f5;
          color: #757575;
        `;
    }
  }}
`;

export const CardBody = styled.div`
  padding: 12px 16px;
`;

export const QuickInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

export const OrderDate = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const DateLabel = styled.span`
  font-size: 11px;
  color: #666;
  font-weight: 500;
`;

export const DateValue = styled.span`
  font-size: 12px;
  color: #333;
  font-weight: 600;
`;

export const ItemCount = styled.span`
  font-size: 12px;
  color: #666;
  background: #f5f5f5;
  padding: 4px 8px;
  border-radius: 6px;
`;

export const ProductPreview = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  overflow-x: auto;
  padding-bottom: 4px;

  &::-webkit-scrollbar {
    height: 2px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 2px;
  }
`;

export const ProductImage = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 6px;
  overflow: hidden;
  background: #f5f5f5;
  border: 1px solid #eee;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const MoreItemsIndicator = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 6px;
  background: #f0f0f0;
  border: 1px solid #eee;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
  color: #666;
  flex-shrink: 0;
`;

export const OrderFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
`;

export const TotalAmount = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #333;
`;

export const ViewDetailsButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  background-color: #f5f5f5;
  border-radius: 6px;
  color: #333;
  font-weight: 500;
  text-decoration: none;
  transition: background-color 0.2s;
  font-size: 12px;

  &:hover {
    background-color: #e0e0e0;
  }

  svg {
    margin-left: 4px;
    width: 12px;
    height: 12px;
  }
`;

// Mobile responsive adjustments
export const MobileOrderCardContainer = styled(OrderCardContainer)`
  @media (max-width: 640px) {
    margin-bottom: 6px;
    border-radius: 6px;
  }
`;

export const MobileCardHeader = styled(CardHeader)`
  @media (max-width: 640px) {
    padding: 10px 12px;
  }
`;

export const MobileCardBody = styled(CardBody)`
  @media (max-width: 640px) {
    padding: 10px 12px;
  }
`;

export const MobileQuickInfo = styled(QuickInfo)`
  @media (max-width: 640px) {
    margin-bottom: 8px;
  }
`;

export const MobileOrderFooter = styled(OrderFooter)`
  @media (max-width: 640px) {
    padding-top: 8px;
  }
`;
